<template>
  <div
    class="dropdown-filter"
    tabindex="1"
    @keydown.enter="toggleOpen">
    <b-dropdown
      ref="filter"
      aria-role="menu"
      expanded
      min-height="auto"
      :loading="loading"
      :disabled="disabled"
      :position="position">
      <template #trigger="{ active }">
        <div
          class="filter-button"
          :class="{ 'is-at-limit': isAtLimit }"
          role="button">
          <div class="button-content">
            <div class="top-labels">
              <div class="top-left-label-group">
                <div class="top-left-label">
                  {{ topLeftLabel }}
                </div>
                <div
                  v-if="selectionCountLabel"
                  class="selection-count"
                  :class="{ 'is-at-limit': isAtLimit }">
                  {{ selectionCountLabel }}
                </div>
              </div>
              <div class="action-buttons">
                <slot name="action-buttons" />
              </div>
            </div>
            <div class="button-content-bottom">
              <div
                v-if="loading"
                class="loading-message">
                Loading
              </div>
              <div
                v-else
                class="button-value">
                {{ selectionSummary }}
              </div>

              <b-icon
                class="icon right dropdown-icon"
                :icon="$screen.is('sm') ? 'menu-right' : active ? 'menu-up' : 'menu-down'" />
            </div>
          </div>
        </div>
      </template>

      <b-dropdown-item
        aria-role="menu-item"
        custom
        paddingless>
        <slot name="before" />
        <form>
          <input
            v-if="enableSearch"
            v-model="searchInput"
            type="text"
            class="input search-input"
            placeholder="Search"
            @keydown.enter.prevent />
          <div
            class="content-container"
            :class="{ 'is-loading': loading }">
            <div
              v-for="(option, i) in resultsToShow"
              :key="i"
              class="dropdown-option"
              :class="{
                'is-selected': isSelected(option),
                'is-disabled': isOptionDisabled(option),
              }"
              @click="handleOptionClick(option)">
              <b-checkbox
                v-if="isMultiSelect"
                :value="isSelected(option)"
                class="selected-checkbox"
                @click.prevent />
              <div
                tabindex="0"
                class="option-clickable"
                @keydown.enter.stop="handleOptionClick(option)">
                {{ option[label] }}
              </div>
            </div>

            <div
              v-if="searchInput && resultsToShow.length === 0"
              class="no-results">
              No results
            </div>

            <div
              v-else-if="resultsToShow.length === 0"
              class="no-results">
              No Options available
            </div>
          </div>
        </form>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
  import FuzzySearch from 'fuzzy-search'

  export default {
    name: 'DashboardSectionControlSelect',
    props: {
      loading: { type: Boolean, default: false, },
      disabled: { type: Boolean, default: false, },
      options: { type: Array, required: true, default: () => [], },
      value: { type: Array, default: () => [], },
      enableSearch: { type: Boolean, default: true, },
      isMultiSelect: { type: Boolean, default: false, },
      multiSelectLimit: { type: Number, default: null, },
      label: { type: String, default: 'name', },
      trackBy: { type: String, default: 'id', },
      topLeftLabel: { type: String, default: '', },
      position: { type: String, default: null, },
    },
    data () {
      return {
        searchInput: '',
      }
    },
    computed: {
      searcher () {
        return new FuzzySearch(this.options, [this.label,])
      },
      resultsToShow () {
        if (this.searchInput) {
          return this.searcher.search(this.searchInput)
        }
        return this.options
      },
      selectedOptions () {
        return this.value
          .map((key) => this.options.find((option) => option[this.trackBy] === key))
          .filter(Boolean)
      },
      isAtLimit () {
        return !!this.multiSelectLimit && this.value.length >= this.multiSelectLimit
      },
      selectionCountLabel () {
        if (!this.isMultiSelect || !this.multiSelectLimit) {
          return ''
        }
        return `${this.value.length}/${this.multiSelectLimit}`
      },
      selectionSummary () {
        const labels = this.selectedOptions.map((item) => item[this.label]).filter(Boolean)
        return labels.length > 0 ? labels.join(', ') : 'Select an option'
      },
    },
    methods: {
      isSelected (option) {
        return this.value.includes(option[this.trackBy])
      },
      isOptionDisabled (option) {
        return this.isMultiSelect && this.isAtLimit && !this.isSelected(option)
      },
      handleOptionClick (option) {
        if (this.isOptionDisabled(option)) {
          return
        }
        if (this.isMultiSelect) {
          this.toggleOption(option)
        } else {
          this.selectSingleOption(option)
        }
      },
      selectSingleOption (option) {
        this.$emit('input', [option[this.trackBy],])
        this.toggleOpen()
      },
      toggleOption (option) {
        const optionKey = option[this.trackBy]
        const nextValue = this.isSelected(option)
          ? this.value.filter((value) => value !== optionKey)
          : [...this.value, optionKey,]
        this.$emit('input', nextValue)
      },
      toggleOpen () {
        this.$refs.filter.toggle()
      },
    },
  }
</script>

<style lang="scss" scoped>
.dropdown-filter {
  width: 100%;
  min-width: 0;

  ::v-deep .dropdown,
  ::v-deep .dropdown-trigger {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
}

.dropdown-option {
  padding: 10px;
  display: flex;
  align-items: center;
  background-color: white;

  &:hover {
    background-color: $grayscale-7;
    cursor: pointer;
    border-radius: 6px;
  }

  &.is-selected {
    background-color: $grayscale-6;
  }

  &.is-disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.option-clickable {
  flex-grow: 1;
}

.selected-checkbox {
  pointer-events: none;
  margin-right: 8px;
}

.search-input {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  border-bottom: $border-1 !important;

  &:focus {
    box-shadow: none !important;
  }
}

.content-container {
  max-height: 400px;
  overflow-y: auto;

  &.is-loading {
    opacity: 0.5;
  }
}

.no-results {
  text-align: center;
  padding: 10px;
  opacity: 0.8;
}

.filter-button {
  border: 1px solid $gray-1;
  background-color: white;
  display: flex;
  align-items: center;
  padding: 8px 15px;
  border-radius: 6px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;

  .icon {
    color: $gray-3;
    font-size: 18px !important;
    flex-shrink: 0;

    &.right {
      margin-left: auto;
      margin-right: 0;
    }
  }

  .button-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .button-content-bottom {
    margin: 0px;
    min-height: 20px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .button-value {
    font-size: 14px;
    color: $grayscale-2;
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.is-at-limit {
    border-color: #d28f00;
    box-shadow: 0 0 0 1px rgba(210, 143, 0, 0.15);
  }
}

.top-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .top-left-label-group {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .top-left-label {
    font-size: 12px;
    color: $grayscale-4;
  }
}

.selection-count {
  font-size: 11px;
  color: $grayscale-4;
  line-height: 1;

  &.is-at-limit {
    color: #a56b00;
  }
}
</style>
